import { Component, OnInit } from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { ParametrosService } from "app/services/parametros.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { VoucherType } from "app/models/voucher-type.models";

@Component({
  selector: "app-talonarios",
  templateUrl: "./create-talonario.component.html",
  styleUrls: ["./create-talonario.component.css"],
})
export class CreateTalonarioComponent implements OnInit {
  form: UntypedFormGroup;
  textoBtnSubmit = "Guardar";
  puntoVentas = [];
  puntoVentasFiltered = []; // Array filtrado según el tipo electrónico
  idTalonario: number;
  tipocomprobantes: VoucherType[] = [];
  tipoTalonarios = [];
  letrasPermitidas = [];
  idForm = '-';

  // Propiedades para controlar el estado de los campos
  isLetterDisabled = true;
  isVoucherTypeDisabled = true;
  isLoadingTalonario = false; // Nueva bandera para controlar la carga inicial
  isElectronicSelected = false; // Nueva bandera para controlar si es electrónico
  hasVouchers = false; // Nueva bandera para controlar si el talonario tiene comprobantes asociados

  constructor(
    private parametrosService: ParametrosService,
    private fb: UntypedFormBuilder,
    private router: Router,
    private routerActive: ActivatedRoute,
    private snackBar: MatSnackBar
  ) {
    this.idTalonario = this.routerActive.snapshot.params.id;
    this.form = this.fb.group({
      id: [1],
      receiptBookTypeId: ["", Validators.required],
      receiptBookLetterId: ["", Validators.required],
      voucherTypeId: ["", Validators.required],
      description: ["", Validators.required],
      salesPointsId: [null], // El required se aplicará dinámicamente según el tipo
      isElectronic: [false],
      observations: "",
      type: ["comercial", Validators.required],
      fromNumber: [1, Validators.required],
      toNumber: [999999, Validators.required],
      currentNumber: [1, Validators.required],
      status: [true, Validators.required], // Nuevo campo status
    });
    // Escuchar cambios en el tipo (comercial/interno) para aplicar validación dinámica al punto de venta
    this.form.get('type').valueChanges.subscribe(typeValue => {
      const salesPointsControl = this.form.get('salesPointsId');
      const isElectronicControl = this.form.get('isElectronic');
      
      if (typeValue === 'comercial') {
        salesPointsControl.setValidators([Validators.required]);
        // Aplicar filtro cuando el tipo sea comercial
        this.filterPuntoVentas();
      } else {
        // Si es interno, limpiar validaciones y valores relacionados
        salesPointsControl.clearValidators();
        salesPointsControl.setValue(null);
        // Para tipo interno, establecer isElectronic como false automáticamente
        isElectronicControl.setValue(false);
        this.isElectronicSelected = false;
        this.updateNumberFieldsState();
      }
      salesPointsControl.updateValueAndValidity();
    });
    this.form.controls.id.disable();
    
    // Inicialmente deshabilitar campos que dependen de selecciones previas
    this.form.get('receiptBookLetterId').disable();
    this.form.get('voucherTypeId').disable();
    this.isLetterDisabled = true;
    this.isVoucherTypeDisabled = true;

    // Escuchar cambios en el tipo de talonario
    this.form.get('receiptBookTypeId').valueChanges.subscribe(receiptBookTypeId => {
      console.log('Cambio en receiptBookTypeId:', receiptBookTypeId);
      // No ejecutar durante la carga inicial del talonario
      if (this.isLoadingTalonario) {
        return;
      }
      if (receiptBookTypeId) {
        this.getLetrasPermitidas(receiptBookTypeId);
        // Llamar a getTiposComprobantes con el tipo correspondiente
        const operationType = this.getOperationTypeByReceiptBookType(receiptBookTypeId);
        console.log('OperationType calculado:', operationType);
        this.getTiposComprobantes(operationType);       
        this.form.get('voucherTypeId').enable();
        this.form.get('voucherTypeId').setValue('');
        this.form.get('receiptBookLetterId').setValue('');
        this.form.get('description').setValue('');
        this.isVoucherTypeDisabled = false;
        // La letra permanece deshabilitada hasta que se seleccione un tipo de comprobante
        this.form.get('receiptBookLetterId').disable();
        this.isLetterDisabled = true;
      } else {
        this.letrasPermitidas = [];
        this.tipocomprobantes = [];
        this.form.get('receiptBookLetterId').disable();
        this.form.get('receiptBookLetterId').setValue('');
        this.form.get('voucherTypeId').disable();
        this.form.get('voucherTypeId').setValue('');
        this.form.get('description').setValue('');
        this.isLetterDisabled = true;
        this.isVoucherTypeDisabled = true;
      }
      this.updateDescription();
    });

    // Escuchar cambios en la letra
    this.form.get('receiptBookLetterId').valueChanges.subscribe(receiptBookLetterId => {
      this.updateDescription();
    });

    // Escuchar cambios en el tipo de comprobante
    this.form.get('voucherTypeId').valueChanges.subscribe(voucherTypeId => {
      // No ejecutar durante la carga inicial del talonario
      if (this.isLoadingTalonario) {
        return;
      }
      
      if (voucherTypeId && voucherTypeId !== '') {
        // Habilitar la letra cuando se selecciona un tipo de comprobante
        this.form.get('receiptBookLetterId').enable();
        this.isLetterDisabled = false;
      } else {
        // Deshabilitar la letra si no hay tipo de comprobante seleccionado
        this.form.get('receiptBookLetterId').disable();
        this.form.get('receiptBookLetterId').setValue('');
        this.isLetterDisabled = true;
      }
      
      this.updateDescription();
    });

    // Validar números cuando cambien
    this.form.get('fromNumber').valueChanges.subscribe(() => {
      this.validateNumbers();
    });

    this.form.get('toNumber').valueChanges.subscribe(() => {
      this.validateNumbers();
    });

    this.form.get('currentNumber').valueChanges.subscribe(() => {
      this.validateNumbers();
    });

    // Escuchar cambios en isElectronic
    this.form.get('isElectronic').valueChanges.subscribe(isElectronic => {
      this.isElectronicSelected = isElectronic;
      this.updateNumberFieldsState();
      this.filterPuntoVentas();
      // Limpiar selección de punto de venta cuando cambia el tipo
      if (this.form.get('type')?.value === 'comercial') {
        this.form.get('salesPointsId')?.setValue('');
      }
    });

  }

  ngOnInit(): void {
    const sidebar = <HTMLElement>document.querySelector(".navbar-brand");
    let titulo;
    if (this.idTalonario) {
      titulo = "Modificar";
      this.textoBtnSubmit = "Modificar";
      this.getTalonarioById();
    } else {
      titulo = "Nuevo";
      this.textoBtnSubmit = "Guardar";
      this.getPuntoDeVenta();
    }
    if (sidebar !== undefined) {
      sidebar.innerHTML = `${titulo} talonario`;
    }

    this.getPuntoDeVenta();
    this.getTiposComprobantes();
    this.getTipoTalonarios();
    // Las letras se cargarán cuando se seleccione un tipo de talonario
  }

  getPuntoDeVenta() {
    this.parametrosService.getPuntosDeVenta().subscribe(
      (data) => {
        this.puntoVentas = data;
        this.filterPuntoVentas(); // Aplicar filtro inicial
      },
      (err) => console.log(err)
    );
  }

  filterPuntoVentas() {
    const isElectronic = this.form.get('isElectronic')?.value;
    if (isElectronic) {
      // Si es electrónico, mostrar solo puntos de venta electrónicos (electronic = 1)
      this.puntoVentasFiltered = this.puntoVentas.filter(punto => punto.electronic === 1);
    } else {
      // Si no es electrónico, mostrar solo puntos de venta no electrónicos (electronic = 0)
      this.puntoVentasFiltered = this.puntoVentas.filter(punto => punto.electronic === 0);
    }
  }

  getTalonarioById() {
    this.isLoadingTalonario = true; // Activar bandera durante la carga
    this.parametrosService.getTalonarioById(this.idTalonario).subscribe(
      (data) => {
        // Si la respuesta es { ...campos... } o { talonario: { ...campos... } }
        const talonario = data.talonario ? data.talonario : data;
        
        console.log('Datos del talonario recibidos:', talonario);
        this.idForm = talonario.id ? talonario.id.toString() : '-'; // Actualizar idForm para mostrar en el título
        
        // Verificar si el talonario tiene comprobantes asociados
        this.checkIfTalonarioHasVouchers();
        
        // Cargar datos dependientes de forma secuencial
        this.loadTalonarioData(talonario);
      },
      (err) => {
        console.log(err);
        this.isLoadingTalonario = false; // Desactivar bandera en caso de error
      }
    );
  }

  private checkIfTalonarioHasVouchers() {
    this.parametrosService.checkTalonarioHasVouchers(this.idTalonario).subscribe(
      (response) => {
        this.hasVouchers = response.hasVouchers;
        console.log('Talonario tiene comprobantes asociados:', this.hasVouchers);
      },
      (err) => {
        console.log('Error al verificar comprobantes asociados:', err);
        // En caso de error, asumir que no tiene comprobantes para permitir edición
        this.hasVouchers = false;
      }
    );
  }

  private loadTalonarioData(talonario: any) {
    // Paso 1: Cargar letras permitidas si hay tipo de talonario
    if (talonario.receiptBookTypeId) {
      this.getLetrasPermitidas(talonario.receiptBookTypeId, () => {
        // Paso 2: Cargar tipos de comprobantes
        const operationType = this.getOperationTypeByReceiptBookType(talonario.receiptBookTypeId);
        this.getTiposComprobantesWithCallback(operationType, () => {
          // Paso 3: Dar un tiempo adicional para asegurar que los datos están disponibles
          setTimeout(() => {
            this.setTalonarioFormValues(talonario);
            this.isLoadingTalonario = false;
          }, 300);
        });
      });
    } else {
      // Si no hay tipo de talonario, cargar tipos de comprobantes por defecto
      this.getTiposComprobantesWithCallback('sales', () => {
        setTimeout(() => {
          this.setTalonarioFormValues(talonario);
          this.isLoadingTalonario = false;
        }, 300);
      });
    }
  }

  private getTiposComprobantesWithCallback(operationType: string, callback: () => void) {
    this.parametrosService.getVoucherTypes(operationType).subscribe(
      (data) => {
        this.tipocomprobantes = data;
        console.log('Tipos de comprobantes cargados:', this.tipocomprobantes);
        
        // Esperar un momento para asegurar que Angular detecte los cambios
        setTimeout(() => {
          callback();
        }, 100);
      },
      (err) => {
        console.error('Error al obtener los tipos de comprobante', err);
        this.openSnackBar('Error al obtener los tipos de comprobante', 'Cerrar');
        callback(); // Llamar callback incluso en error para continuar el flujo
      }
    );
  }

  private setTalonarioFormValues(talonario: any) {
    console.log('Estableciendo valores en el formulario:', talonario);
    console.log('Letras disponibles:', this.letrasPermitidas);
    console.log('Tipos de comprobantes disponibles:', this.tipocomprobantes);
    
    // Verificar que los datos dependientes estén disponibles
    if (talonario.receiptBookTypeId && (!this.letrasPermitidas || this.letrasPermitidas.length === 0)) {
      console.warn('Letras no están disponibles aún, reintentando...');
      setTimeout(() => this.setTalonarioFormValues(talonario), 500);
      return;
    }
    
    if (talonario.voucherTypeId && (!this.tipocomprobantes || this.tipocomprobantes.length === 0)) {
      console.warn('Tipos de comprobantes no están disponibles aún, reintentando...');
      setTimeout(() => this.setTalonarioFormValues(talonario), 500);
      return;
    }
    
    // Convertir los IDs a números para asegurar que coincidan con las opciones del select
    const receiptBookTypeId = talonario.receiptBookTypeId ? Number(talonario.receiptBookTypeId) : '';
    const receiptBookLetterId = talonario.receiptBookLetterId ? Number(talonario.receiptBookLetterId) : '';
    const voucherTypeId = talonario.voucherTypeId ? Number(talonario.voucherTypeId) : '';
    const salesPointsId = talonario.salesPointsId ? Number(talonario.salesPointsId) : '';
    
    // Verificar que el tipo de comprobante existe en la lista
    if (voucherTypeId && this.tipocomprobantes.length > 0) {
      const tipoExists = this.tipocomprobantes.find(tipo => tipo.id === voucherTypeId);
      if (!tipoExists) {
        console.warn('Tipo de comprobante no encontrado en la lista:', voucherTypeId);
      } else {
        console.log('Tipo de comprobante encontrado:', tipoExists);
      }
    }
    
    // Establecer valores del formulario
    this.form.patchValue({
      id: talonario.id,
      receiptBookTypeId: receiptBookTypeId,
      voucherTypeId: voucherTypeId,
      receiptBookLetterId: receiptBookLetterId,
      description: talonario.description || '',
      salesPointsId: salesPointsId,
      isElectronic: talonario.isElectronic !== undefined ? talonario.isElectronic : false,
      observations: talonario.observations || '',
      type: talonario.type || 'comercial', // Incluir el tipo con valor por defecto
      fromNumber: talonario.fromNumber || 1,
      toNumber: talonario.toNumber || 999999,
      currentNumber: talonario.currentNumber || 1,
      status: talonario.status !== undefined ? talonario.status : true
    });
    
    // Verificación adicional: si el tipo de comprobante no se seteó correctamente, intentar de nuevo
    setTimeout(() => {
      if (voucherTypeId && this.form.get('voucherTypeId').value !== voucherTypeId) {
        console.log('Reaplicando valor de voucherTypeId:', voucherTypeId);
        this.form.get('voucherTypeId').setValue(voucherTypeId);
      }
      if (receiptBookLetterId && this.form.get('receiptBookLetterId').value !== receiptBookLetterId) {
        console.log('Reaplicando valor de receiptBookLetterId:', receiptBookLetterId);
        this.form.get('receiptBookLetterId').setValue(receiptBookLetterId);
      }
    }, 100);
    
    console.log('Valores después de patchValue:', this.form.value);
    
    // Aplicar filtro de puntos de venta después de cargar los datos
    this.filterPuntoVentas();
    
    // Deshabilitar el campo ID
    this.form.controls.id.disable();
    
    // Deshabilitar campos críticos si el talonario tiene comprobantes asociados
    if (this.hasVouchers) {
      this.form.get('receiptBookTypeId').disable();
      this.form.get('voucherTypeId').disable();
      this.form.get('receiptBookLetterId').disable();
      this.form.get('salesPointsId').disable();
      this.form.get('isElectronic').disable();
      this.form.get('fromNumber').disable();
      this.form.get('toNumber').disable();
      this.form.get('currentNumber').disable();
    } else {
      // Habilitar campos dependientes según los datos disponibles
      if (receiptBookTypeId) {
        this.form.get('voucherTypeId').enable();
        this.isVoucherTypeDisabled = false;
        
        if (voucherTypeId) {
          this.form.get('receiptBookLetterId').enable();
          this.isLetterDisabled = false;
        }
      }
    }
    
    // Forzar la actualización de la descripción
    setTimeout(() => {
      this.updateDescription();
      // Actualizar el estado de los campos numéricos basado en isElectronic
      this.isElectronicSelected = this.form.get('isElectronic').value;
      this.updateNumberFieldsState();
    }, 200);
  }

  getTiposComprobantes(operationType: string = 'sales') {
    console.log('Obteniendo tipos de comprobante para operationType:', operationType);
    this.parametrosService.getVoucherTypes(operationType).subscribe(
      (data) => {
        console.log('Tipos de comprobante recibidos:', data);
        this.tipocomprobantes = data;
      },
      (err) => {
        console.error('Error al obtener los tipos de comprobante', err);
        this.openSnackBar('Error al obtener los tipos de comprobante', 'Cerrar');
      }
    );
  }

  getTipoTalonarios() {
    this.parametrosService.getReceiptBookTypes().subscribe(
      (data) => {
        this.tipoTalonarios = data;
      },
      (err) => console.log(err)
    );
  }

  getLetrasPermitidas(receiptBookTypeId?: number, callback?: () => void) {
    this.parametrosService.getReceiptBookLetters(receiptBookTypeId).subscribe(
      (data) => {
        this.letrasPermitidas = data;
        // Llamar a updateDescription después de cargar las letras
        setTimeout(() => {
          this.updateDescription();
          // Ejecutar el callback si se proporcionó
          if (callback) {
            callback();
          }
        }, 100);
      },
      (err) => console.log(err)
    );
  }

  updateDescription() {
    const voucherTypeId = this.form.get('voucherTypeId').value;
    const receiptBookLetterId = this.form.get('receiptBookLetterId').value;
    const receiptBookTypeId = this.form.get('receiptBookTypeId').value;
    
    let description = '';
    
    // Solo generar descripción si tenemos tanto el tipo de comprobante como la letra
    if (voucherTypeId && receiptBookLetterId && 
        voucherTypeId !== '' && voucherTypeId !== 'undefined' && voucherTypeId !== null &&
        receiptBookLetterId !== '' && receiptBookLetterId !== 'undefined' && receiptBookLetterId !== null) {
      
      // Obtener el nombre del tipo de comprobante usando la estructura correcta
      const tipoComprobante = this.tipocomprobantes.find(tipo => tipo.id === Number(voucherTypeId));
      const nombreTipoComprobante = tipoComprobante && tipoComprobante.voucherTypesCode ? 
        tipoComprobante.voucherTypesCode.name : '';
      
      // Obtener la letra usando el ID
      const letraObj = this.letrasPermitidas.find(l => l.id === Number(receiptBookLetterId));
      const letraTexto = letraObj ? letraObj.letter : '';
      
      // Obtener el nombre del tipo de talonario
      const tipoTalonario = this.tipoTalonarios.find(tipo => tipo.id === Number(receiptBookTypeId));
      const nombreTipoTalonario = tipoTalonario ? tipoTalonario.name : '';
      
      // Generar descripción con el formato: "${nombreTipoComprobante} ${letra} (${tipoTalonario})"
      if (nombreTipoComprobante && letraTexto && nombreTipoTalonario) {
        description = `${nombreTipoComprobante} ${letraTexto} (${nombreTipoTalonario})`;
      }
    }
    
    // Establecer la descripción
    this.form.get('description').setValue(description);
  }

  validateNumbers() {
    // Si es electrónico, no validar los números
    if (this.isElectronicSelected) {
      return true;
    }

    const fromNumber = this.form.get('fromNumber').value;
    const toNumber = this.form.get('toNumber').value;
    const currentNumber = this.form.get('currentNumber').value;

    // Validar que el número inicial sea menor al final
    if (fromNumber && toNumber && fromNumber >= toNumber) {
      this.form.get('toNumber').setErrors({ invalidRange: true });
      return false;
    }

    // Validar que el número actual esté en el rango
    if (currentNumber && fromNumber && toNumber) {
      if (currentNumber < fromNumber || currentNumber > toNumber) {
        this.form.get('currentNumber').setErrors({ outOfRange: true });
        return false;
      }
    }

    // Limpiar errores si todo está bien
    this.form.get('toNumber').setErrors(null);
    this.form.get('currentNumber').setErrors(null);
    return true;
  }

  updateNumberFieldsState() {
    if (this.isElectronicSelected) {
      // Si es electrónico, hacer currentNumber de solo lectura y ocultar fromNumber y toNumber
      this.form.get('currentNumber').disable();
      this.form.get('fromNumber').clearValidators();
      this.form.get('toNumber').clearValidators();
      this.form.get('currentNumber').clearValidators();
      
      // Establecer valores por defecto para campos ocultos
      this.form.get('fromNumber').setValue(1);
      this.form.get('toNumber').setValue(999999);
      // Para electrónicos, el número actual inicia en 1
      if (!this.form.get('currentNumber').value) {
        this.form.get('currentNumber').setValue(1);
      }
    } else {
      // Si no es electrónico, habilitar todos los campos y restaurar validaciones
      this.form.get('currentNumber').enable();
      this.form.get('fromNumber').setValidators([Validators.required]);
      this.form.get('toNumber').setValidators([Validators.required]);
      this.form.get('currentNumber').setValidators([Validators.required]);
    }
    
    // Actualizar validaciones
    this.form.get('fromNumber').updateValueAndValidity();
    this.form.get('toNumber').updateValueAndValidity();
    this.form.get('currentNumber').updateValueAndValidity();
  }

  guardar() {
    // Verificar si es una modificación de un talonario con comprobantes asociados
    if (this.idTalonario && this.hasVouchers) {
      // Solo permitir modificar observaciones y estado cuando tiene comprobantes asociados
      const talonarioData = {
        observations: this.form.get('observations').value,
        status: this.form.get('status').value
      };
      
      this.parametrosService.modificarTalonario(talonarioData as any, this.idTalonario)
        .subscribe(
          (data) => {
            this.openSnackBar('Talonario modificado exitosamente', 'Cerrar');
            this.router.navigate(["configurar/talonarios"]);
          },
          (err) => {
            console.log(err);
            this.openSnackBar('Error al modificar el talonario', 'Cerrar');
          }
        );
      return;
    }

    if (this.form.invalid) {
      this.markFormGroupTouched(this.form);
      this.openSnackBar('Por favor, complete todos los campos obligatorios', 'Cerrar');
      return;
    }

    if (!this.validateNumbers()) {
      this.openSnackBar('Por favor, verifique los números del talonario', 'Cerrar');
      return;
    }

    // Preparar los datos exactamente como los requiere el backend
    const salesPointsIdValue = this.form.get('salesPointsId').value;
    const talonarioData = {
      receiptBookTypeId: Number(this.form.get('receiptBookTypeId').value),
      receiptBookLetterId: Number(this.form.get('receiptBookLetterId').value),
      voucherTypeId: Number(this.form.get('voucherTypeId').value),
      description: this.form.get('description').value,
      salesPointsId: salesPointsIdValue && salesPointsIdValue !== '' ? Number(salesPointsIdValue) : null,
      isElectronic: this.form.get('isElectronic').value,
      observations: this.form.get('observations').value,
      type: this.form.get('type').value, // Incluir el tipo (comercial/interno)
      fromNumber: this.isElectronicSelected ? 1 : Number(this.form.get('fromNumber').value),
      toNumber: this.isElectronicSelected ? 999999 : Number(this.form.get('toNumber').value),
      currentNumber: this.isElectronicSelected ? (Number(this.form.get('currentNumber').value) || 1) : Number(this.form.get('currentNumber').value),
      status: this.form.get('status').value // Incluir status
    };

    if (this.idTalonario) {
      this.parametrosService.modificarTalonario(talonarioData as any, this.idTalonario)
        .subscribe(
          (data) => {
            this.openSnackBar('Talonario modificado exitosamente', 'Cerrar');
            this.router.navigate(["configurar/talonarios"]);
          },
          (err) => {
            console.log(err);
            this.openSnackBar('Error al modificar el talonario', 'Cerrar');
          }
        );
    } else {
      this.parametrosService.guardarTalonario(talonarioData as any).subscribe(
        (data) => {
          this.openSnackBar('Talonario guardado exitosamente', 'Cerrar');
          this.router.navigate(["configurar/talonarios"]);
        },
        (err) => {
          console.log(err);
          this.openSnackBar('Error al guardar el talonario', 'Cerrar');
        }
      );
    }
  }

  getErrorMessage(fieldName: string): string {
    const field = this.form.get(fieldName);
    if (field?.errors && field.touched) {
      if (field.errors['required']) {
        return `${this.getFieldLabel(fieldName)} es requerido`;
      }
      if (field.errors['invalidRange']) {
        return 'El número final debe ser mayor al inicial';
      }
      if (field.errors['outOfRange']) {
        return 'El número actual debe estar entre el inicial y final';
      }
    }
    return '';
  }

  private getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      'receiptBookTypeId': 'El tipo de talonario',
      'receiptBookLetterId': 'La letra',
      'voucherTypeId': 'El tipo de comprobante',
      'description': 'La descripción',
      'salesPointsId': 'El punto de venta',
      'type': 'El tipo de talonario',
      'fromNumber': 'El número inicial',
      'toNumber': 'El número final',
      'currentNumber': 'El número actual'
    };
    return labels[fieldName] || 'El campo';
  }

  // Método para verificar si un campo específico tiene errores
  hasError(fieldName: string): boolean {
    const field = this.form.get(fieldName);
    return !!(field?.errors && field.touched);
  }

  private markFormGroupTouched(formGroup: UntypedFormGroup) {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control.markAsTouched();
      if (control instanceof UntypedFormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  openSnackBar(message: string, action: string) {
    this.snackBar.open(message, action, {
      duration: 5000,
      verticalPosition: 'top',
    });
  }

  /**
   * Mapea el tipo de talonario (receiptBookTypeId) al tipo de operación para obtener los comprobantes correctos
   */
  private getOperationTypeByReceiptBookType(receiptBookTypeId: number): string {
    console.log('Recibiendo ID de tipo de talonario:', receiptBookTypeId);
    console.log('Tipos de talonario disponibles:', this.tipoTalonarios);
    
    // Buscar el tipo de talonario por ID en el array cargado desde receipt-book-types
    const tipoTalonario = this.tipoTalonarios.find(tipo => tipo.id === Number(receiptBookTypeId));
    
    if (tipoTalonario) {
      console.log('Tipo de talonario encontrado:', tipoTalonario);
      
      // Mapear según el nombre del tipo de talonario obtenido del endpoint receipt-book-types
      const nombre = (tipoTalonario.name || '').toLowerCase();
      
      // Mapeo basado en los nombres del endpoint receipt-book-types
      switch (nombre) {
        case 'venta':
          return 'sales';
        case 'presupuesto':
          return 'sales'; // Los presupuestos también son de venta
        case 'compra':
          return 'purchases';
        case 'pago':
          return 'purchases'; // Los pagos pueden ser de compra
        case 'stock':
          return 'stock';
        case 'remito':
          return 'stock'; // Los remitos están relacionados con stock
        case 'tesoreria':
          return 'treasury';
        default:
          console.log('Nombre no reconocido, usando fallback por ID');
          break;
      }
    } else {
      console.log('Tipo de talonario no encontrado en el array');
    }
    
    // Fallback por ID si no se encuentra por nombre o el nombre no coincide
    switch (Number(receiptBookTypeId)) {
      case 1: // ID 1
        return 'sales';
      case 2: // ID 2
        return 'sales';
      case 3: // ID 3
        return 'purchases';
      case 4: // ID 4
        return 'stock';
      case 5: // ID 5
        return 'stock';
      case 6: // ID 6
        return 'treasury';
      case 7: // ID 7
        return 'treasury';
      default:
        console.log('ID no reconocido, usando default sales');
        return 'sales'; // Default a ventas
    }
  }
}
